import { createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import {
    fetchDocuments,
    fetchDocumentDetails,
    deleteDocument,
    createDocument, fetchDocumentTypes, fetchPropertyTypes, patchDocumentDetails, fetchVerificationView, verifyDocument
} from './documentsThunks';

import {
    createProperty,
    updateProperty,
    deleteProperty, fetchPropertyDetails, patchPropertyDetails
} from './propertiesThunks';

import {DocumentsState, Document, PropertyFull, PropertyType, DocumentType, VerificationView} from '../../../types';
import {
    comparePropertiesArrays,
    comparePropertyWithProperties,
    compareTwoPropertiesThunk
} from "./propertiesComparisonThunks";

const initialState: DocumentsState = {
    documents: {},
    loading: false,
    isComputing: false, // <-- Новое поле
    error: null,
    lastProcessedDocument: null,
    propertiesComparisonCache: {},
    loaded: false,
    documentTypes: [],
    propertyTypes: []
};

const documentsSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
        // Если потребуется, можно добавить синхронные редьюсеры для документов
    },
    extraReducers: (builder: ActionReducerMapBuilder<DocumentsState>) => {
        //
        // 1) fetchDocuments
        //
        builder
            .addCase(fetchDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDocuments.fulfilled, (state, action) => {
                // Просто мёржим новые лайт-документы в state.documents
                state.documents = {
                    ...state.documents,
                    ...action.payload, // payload – это Record<string, Document>
                };
                state.loading = false;
                state.loaded = true;
            })
            .addCase(fetchDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка загрузки документов';
            });

        //
        // 2) fetchDocumentDetails
        //
        builder
            .addCase(fetchDocumentDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDocumentDetails.fulfilled, (state, action: PayloadAction<Document>) => {
                const docFull = action.payload;
                // Забираем список propertyTypes из state
                const { propertyTypes } = state;

                // Для каждого свойства находить объект типа
                docFull.properties = docFull.properties!.map(prop => {
                    const foundType = propertyTypes.find(pt => pt.id === prop.propertyTypeId) || null;
                    return {
                        ...prop,
                        type: foundType, // присваиваем поле .type
                    };
                });

                state.documents[docFull.id] = {
                    ...docFull,
                    detailsLoaded: true,
                };
                state.loading = false;
            })
            .addCase(fetchDocumentDetails.rejected, (state, action) => {
                if (action.payload === 'DETAILS_ALREADY_LOADED') {
                    // игнорируем
                } else {
                    state.error = action.payload || 'Ошибка загрузки деталей документа';
                }
                state.loading = false;
            });

        //
        // 3) createDocument
        //
        builder
            .addCase(createDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createDocument.fulfilled, (state, action) => {
                // возвращаем documentId (string)
                const docId = action.payload;
                state.lastProcessedDocument = docId;
                state.loading = false;
            })
            .addCase(createDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка при создании документа';
            });

        //
        // 4) deleteDocument
        //
        builder
            .addCase(deleteDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteDocument.fulfilled, (state, action: PayloadAction<string>) => {
                delete state.documents[action.payload];
                state.loading = false;
            })
            .addCase(deleteDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка при удалении документа';
            });

        //
        // DocumentTypes + PropertyTypes
        //
        builder
            .addCase(fetchDocumentTypes.pending, (_state) => {
                // при желании можно показать спиннер, либо завести отдельное поле loadingTypes
                // state.loading = true; // опционально
            })
            .addCase(fetchDocumentTypes.fulfilled, (state, action: PayloadAction<DocumentType[]>) => {
                state.documentTypes = action.payload;
                // state.loading = false; // если нужно
            })
            .addCase(fetchDocumentTypes.rejected, (state, action) => {
                // state.loading = false; // если нужно
                state.error = action.payload || 'Ошибка загрузки типов документов';
            })
            .addCase(fetchPropertyTypes.fulfilled, (state, action: PayloadAction<PropertyType[]>) => {
                state.propertyTypes = action.payload;
                const propertyTypesById = new Map(action.payload.map((pt) => [pt.id, pt]));
                Object.values(state.documents).forEach((doc) => {
                    if (!doc.properties) {
                        return;
                    }
                    doc.properties = doc.properties.map((prop) => ({
                        ...prop,
                        type: propertyTypesById.get(prop.propertyTypeId) || null,
                    }));
                });
            })
            .addCase(fetchPropertyTypes.rejected, (state, action) => {
                state.error = action.payload || 'Ошибка загрузки типов свойств';
            });

        //
        // ============ Cоздание / обновление / удаление свойства ============
        // (прежде они возвращали DocumentLight, но сейчас
        //  мы можем ничего не делать тут при fulfilled, если ListenerMiddleware догрузит)
        //
        builder
            .addCase(createProperty.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createProperty.fulfilled, (state, _action) => {
                // По текущей логике: возвращаем { operationId, propertyId }
                // Мы можем вообще ничего не делать тут, так как опираемся
                // на ListenerMiddleware -> pollOperationStatus -> "CREATE_PROPERTY" -> fetchPropertyDetails
                // Но если хотим сразу добавить property в documents, то нужно иметь propertyLight:
                // ...
                state.loading = false;
            })
            .addCase(createProperty.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка при создании свойства';
            });

        builder
            .addCase(updateProperty.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateProperty.fulfilled, (state, _action) => {
                // Возвращаем { operationId, propertyId }, + oldDocumentId?
                // Можно ничего не делать, рассчитывая на fetchPropertyDetails в listener
                state.loading = false;
            })
            .addCase(updateProperty.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка при обновлении свойства';
            });

        builder
            .addCase(deleteProperty.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteProperty.fulfilled, (state, _action) => {
                // action.payload = propertyId (string) или { propertyId, parentDocumentId } и т.п.
                // можно удалить из documents[docId].properties
                state.loading = false;
            })
            .addCase(deleteProperty.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка при удалении свойства';
            });

        //
        // patchDocumentDetails
        //
        builder
            .addCase(patchDocumentDetails.fulfilled, (state, action) => {
                const { documentId, details } = action.payload;
                if (state.documents[documentId]) {
                    state.documents[documentId].details = details;
                }
            });

        //
        // patchPropertyDetails
        //
        builder
            .addCase(patchPropertyDetails.fulfilled, (state, action) => {
                const { propertyId, details } = action.payload;
                // Находим свойство во всех документах (или можно оптимизировать, если знать documentId)
                Object.values(state.documents).forEach(doc => {
                    if (doc.properties) {
                        const prop = doc.properties.find(p => p.id === propertyId);
                        if (prop) {
                            prop.details = details;
                        }
                    }
                });
            });

        //
        // fetchVerificationView
        //
        builder
            .addCase(fetchVerificationView.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchVerificationView.fulfilled, (state, action: PayloadAction<VerificationView>) => {
                const view = action.payload;
                const { propertyTypes } = state;

                // Обновляем документ и его свойства
                if (view.documentDetails.length > 0) {
                    // Предполагаем, что свойства в VerificationView относятся к одному документу
                    // На самом деле VerificationView содержит properties уже с деталями
                    const docId = view.properties.length > 0 ? view.properties[0].documentId : null;
                    
                    if (docId && state.documents[docId]) {
                        state.documents[docId].details = view.documentDetails;
                        state.documents[docId].properties = view.properties.map(prop => ({
                            ...prop,
                            type: propertyTypes.find(pt => pt.id === prop.propertyTypeId) || null,
                            details: prop.details || []
                        }));
                        state.documents[docId].detailsLoaded = true;
                    }
                }
                state.loading = false;
            })
            .addCase(fetchVerificationView.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Ошибка загрузки представления верификации';
            });

        //
        // verifyDocument
        //
        builder
            .addCase(verifyDocument.fulfilled, (state, action) => {
                const { documentId, verificationStatus } = action.payload;
                if (state.documents[documentId]) {
                    // Обновляем статус в деталях
                    const details = state.documents[documentId].details || [];
                    const statusDetail = details.find(d => d.code === 'verification_status');
                    if (statusDetail) {
                        statusDetail.value = verificationStatus;
                    } else {
                        details.push({
                            code: 'verification_status',
                            value: verificationStatus,
                            name: 'Статус верификации',
                            definitionId: ''
                        });
                    }
                    state.documents[documentId].details = [...details];
                }
            });

        builder.addCase(fetchPropertyDetails.fulfilled, (state, action) => {
            const propertyFull = action.payload;
            const { propertyTypes } = state;

            const foundType = propertyTypes.find(pt => pt.id === propertyFull.propertyTypeId) || null;
            propertyFull.type = foundType;

            // Найдём документ, которому принадлежит это свойство
            const doc = state.documents[propertyFull.documentId];
            // Проверим, что документ существует и у него есть поле "properties"
            if (doc && 'properties' in doc && doc.detailsLoaded) {
                // Найдём индекс свойства в массиве
                const idx = doc.properties!.findIndex(p => p.id === propertyFull.id);
                if (idx >= 0) {
                    // Обновляем существующее свойство
                    doc.properties![idx] = propertyFull;
                } else {
                    // Добавляем новое свойство (после создания через API)
                    doc.properties!.push(propertyFull);
                }
            }
        });

        // 7. Сравнение двух свойств
        // @ts-ignore
        // @ts-ignore
        builder
            .addCase(compareTwoPropertiesThunk.pending, (state) => {
                state.isComputing = true;
                state.error = null;
            })
            .addCase(
                compareTwoPropertiesThunk.fulfilled,
                (state, action: PayloadAction<{ propA: PropertyFull; propB: PropertyFull; similarity: [number, number] }>) => {
                    const { propA, propB, similarity } = action.payload;
                    // Здесь ключ генерируется на основе свойств, например, по их ID и типу
                    const cacheKey = `${propA.id}-${propB.id}`;
                    if (!(cacheKey in state.propertiesComparisonCache)) {
                        state.propertiesComparisonCache[cacheKey] = similarity;
                    }
                    state.isComputing = false;
                }
            )
            .addCase(compareTwoPropertiesThunk.rejected, (state, _action) => {
                state.isComputing = true;
                state.error = 'Ошибка при сравнении свойств';
            })

            // 8. Сравнение свойства с массивом свойств
            .addCase(comparePropertyWithProperties.pending, (state) => {
                state.isComputing = true;
                state.error = null;
            })
            .addCase(
                comparePropertyWithProperties.fulfilled,
                (state, action) => {
                    action.payload.forEach(({ propA, propB, similarity }) => {
                        const cacheKey = `${propA.id}-${propB.id}`;
                        if (!(cacheKey in state.propertiesComparisonCache)) {
                            state.propertiesComparisonCache[cacheKey] = similarity;
                        }
                    });
                    state.isComputing = false;
                }
            )
            .addCase(comparePropertyWithProperties.rejected, (state, _action) => {
                state.isComputing = true;
                state.error = 'Ошибка при сравнении свойств';
            })

            // 9. Сравнение массивов свойств
            .addCase(comparePropertiesArrays.pending, (state) => {
                state.isComputing = true;
                state.error = null;
            })
            .addCase(
                comparePropertiesArrays.fulfilled,
                (state, action) => {
                    action.payload.forEach((row) => {
                        row.forEach(({ propA, propB, similarity }) => {
                            const cacheKey = `${propA.id}-${propB.id}`;
                            if (!(cacheKey in state.propertiesComparisonCache)) {
                                state.propertiesComparisonCache[cacheKey] = similarity;
                            }
                        });
                    });
                    state.isComputing = false;
                }
            )
            .addCase(comparePropertiesArrays.rejected, (state, _action) => {
                state.isComputing = false;
                state.error = 'Ошибка при сравнении массивов свойств';
            });
    },
});

export default documentsSlice.reducer;
